import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Menu, X, User, LogOut, PenSquare, LayoutGrid, ChevronDown, Mail } from "lucide-react";
import AuthDialog from "@/components/AuthDialog";
import { WaitlistDialog } from "@/components/ComingSoonRibbon";
import { CATEGORIES } from "@/lib/categories";
import { useSiteInfo, isComingSoon } from "@/hooks/useSiteInfo";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const info = useSiteInfo();
  const comingSoon = isComingSoon(info);
  const isAdmin = user && user.role === "admin";

  const openAuth = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
    setMobileOpen(false);
  };

  return (
    <>
      <header
        data-testid="navbar"
        className="sticky top-0 z-40 bg-[#F8FAFC]/85 backdrop-blur-md border-b border-[#E2E8F0]"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 lg:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="navbar-brand"
            className="flex items-baseline gap-2 group"
          >
            <span className="font-serif text-2xl lg:text-[26px] font-semibold tracking-tight text-[#1A1A1A]">
              buscocompartir
            </span>
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.18em] text-[#3B5FB8]">
              Anuncios · Servicios
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-categories-trigger"
                  className="link-underline text-sm font-medium text-[#1A1A1A] hover:text-[#3B5FB8] transition-colors inline-flex items-center gap-1"
                >
                  Categorías
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="rounded-none border-[#E2E8F0] p-0 min-w-[300px]">
                <div className="grid grid-cols-2 gap-0">
                  {CATEGORIES.map((c) => (
                    <DropdownMenuItem
                      key={c.slug}
                      data-testid={`nav-link-${c.slug}`}
                      onClick={() => c.active && navigate(`/anuncios?categoria=${c.slug}`)}
                      disabled={!c.active}
                      className={`rounded-none py-3 border-r border-b border-[#EEF2F7] font-medium ${
                        !c.active ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <span className="flex-1">{c.label}</span>
                      {!c.active && (
                        <span className="ml-2 text-[9px] font-mono uppercase tracking-widest text-[#64748B]">Próx.</span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </div>
                <div className="p-3 border-t border-[#EEF2F7]">
                  <Link to="/anuncios" data-testid="nav-all-ads" className="text-xs font-mono uppercase tracking-widest text-[#3B5FB8]">
                    Ver todos los anuncios →
                  </Link>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link
              to="/anuncios"
              data-testid="nav-link-all"
              className="link-underline text-sm font-medium text-[#1A1A1A] hover:text-[#3B5FB8] transition-colors"
            >
              Todos
            </Link>
            <Link
              to="/premium"
              data-testid="nav-link-premium"
              className="link-underline text-sm font-semibold text-[#3B5FB8] hover:text-[#2E4A94] transition-colors"
            >
              Premium
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            {user && user.id ? (
              <>
                {!comingSoon || isAdmin ? (
                  <Button
                    data-testid="publish-ad-button"
                    onClick={() => navigate("/publicar")}
                    className="hidden sm:inline-flex bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] rounded-none px-5 h-10 font-medium"
                  >
                    <PenSquare className="w-4 h-4 mr-2" />
                    Publicar anuncio
                  </Button>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="user-menu-trigger"
                      className="flex items-center gap-2 h-10 px-3 border border-[#E2E8F0] hover:border-[#3B5FB8] transition-colors"
                    >
                      <div className="w-7 h-7 bg-[#EEF2F7] text-[#3B5FB8] flex items-center justify-center font-semibold text-sm">
                        {user.name?.[0]?.toUpperCase() || "U"}
                      </div>
                      <span className="hidden md:inline text-sm font-medium text-[#1A1A1A] max-w-[100px] truncate">
                        {user.name || "Cuenta"}
                      </span>
                      {user.is_premium && (
                        <span className="hidden md:inline text-[9px] font-mono uppercase tracking-widest bg-[#3B5FB8] text-[#F8FAFC] px-1.5 py-0.5" data-testid="premium-badge">
                          Premium
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none border-[#E2E8F0] min-w-[200px]">
                    <DropdownMenuItem data-testid="menu-my-ads" onClick={() => navigate("/mis-anuncios")}>
                      <LayoutGrid className="w-4 h-4 mr-2" /> Mis anuncios
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-publish" onClick={() => navigate("/publicar")}>
                      <PenSquare className="w-4 h-4 mr-2" /> Publicar anuncio
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-premium" onClick={() => navigate("/premium")}>
                      <span className="w-4 h-4 mr-2 inline-flex items-center justify-center text-[#3B5FB8]">★</span>
                      {user.is_premium ? "Mi Premium" : "Hacerme Premium"}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-advertisers" onClick={() => navigate("/anunciantes")}>
                      <span className="w-4 h-4 mr-2 inline-flex items-center justify-center text-[#3B5FB8]">◇</span>
                      Contratar banner
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <>
                        <DropdownMenuItem data-testid="menu-admin-students" onClick={() => navigate("/admin/estudiantes")}>
                          <span className="w-4 h-4 mr-2 inline-flex items-center justify-center text-[#3B5FB8]">◆</span>
                          Admin · Estudiantes
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="menu-admin-waitlist" onClick={() => navigate("/admin/lista-espera")}>
                          <span className="w-4 h-4 mr-2 inline-flex items-center justify-center text-[#3B5FB8]">◆</span>
                          Admin · Lista de espera
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                      <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {comingSoon ? (
                  <Button
                    data-testid="navbar-waitlist"
                    onClick={() => setWaitlistOpen(true)}
                    className="bg-[#1E3A8A] hover:bg-[#152B6B] text-[#F8FAFC] rounded-none px-3 sm:px-5 h-10 font-medium text-xs sm:text-sm"
                  >
                    <Mail className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Empresas y profesionales</span>
                    <span className="sm:hidden">Empresas</span>
                  </Button>
                ) : (
                  <>
                    <button
                      data-testid="open-login-button"
                      onClick={() => openAuth("login")}
                      className="hidden sm:inline text-sm font-medium text-[#1A1A1A] hover:text-[#3B5FB8] transition-colors link-underline"
                    >
                      Entrar
                    </button>
                    <Button
                      data-testid="open-register-button"
                      onClick={() => openAuth("register")}
                      className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] rounded-none px-5 h-10 font-medium"
                    >
                      Crear cuenta
                    </Button>
                  </>
                )}
              </>
            )}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden w-10 h-10 border border-[#E2E8F0] flex items-center justify-center"
              aria-label="Menú"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-[#E2E8F0] bg-white max-h-[70vh] overflow-y-auto">
            <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
              <p className="eyebrow py-2">Categorías</p>
              {CATEGORIES.map((c) => (
                <button
                  key={c.slug}
                  data-testid={`mobile-nav-${c.slug}`}
                  onClick={() => {
                    if (!c.active) return;
                    navigate(`/anuncios?categoria=${c.slug}`);
                    setMobileOpen(false);
                  }}
                  disabled={!c.active}
                  className={`py-2 text-sm font-medium text-left flex items-center justify-between ${
                    c.active ? "text-[#1A1A1A]" : "text-[#94A3B8] cursor-not-allowed"
                  }`}
                >
                  <span>{c.label}</span>
                  {!c.active && (
                    <span className="text-[9px] font-mono uppercase tracking-widest text-[#64748B]">Próx.</span>
                  )}
                </button>
              ))}
              <div className="h-px bg-[#E2E8F0] my-2" />
              <Link to="/premium" onClick={() => setMobileOpen(false)} className="py-2 text-[#3B5FB8] font-semibold">
                Premium
              </Link>
              {user && user.id ? (
                <>
                  <Link to="/publicar" onClick={() => setMobileOpen(false)} className="py-2 text-[#1A1A1A] font-medium">
                    Publicar anuncio
                  </Link>
                  <Link to="/mis-anuncios" onClick={() => setMobileOpen(false)} className="py-2 text-[#1A1A1A] font-medium">
                    Mis anuncios
                  </Link>
                </>
              ) : (
                <button onClick={() => openAuth("login")} className="py-2 text-left text-[#1A1A1A] font-medium">
                  Entrar
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} mode={authMode} setMode={setAuthMode} />
      <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} contactEmail={info?.contact_email} />
    </>
  );
}
